// comments in English (clean code, keep design)
// Add a simple math challenge (+/-) as a human verification

import React, { useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import HelmetInfo from "Components/HelmetInfo/HelmetInfo";
import BreadCrumb from "./BreadCrumb/BreadCrumb";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import contentApi from "api/contentApi";
import "react-toastify/dist/ReactToastify.css";

// comment: generate a +/- challenge with non-negative result for subtraction
function genChallenge() {
  let a = Math.floor(Math.random() * 9) + 1; // 1..9
  let b = Math.floor(Math.random() * 9) + 1; // 1..9
  const op = Math.random() < 0.5 ? "+" : "-";
  if (op === "-" && b > a) [a, b] = [b, a];
  return { a, b, op };
}

const FormService = () => {
  const { id } = useParams();
  const { t } = useTranslation("contact");
  const location = useLocation();
  const { name, type } = location.state || {};

  // comment: recaptcha-like local state
  const [challenge, setChallenge] = useState(genChallenge());
  const expected = useMemo(
    () => (challenge.op === "+" ? challenge.a + challenge.b : challenge.a - challenge.b),
    [challenge]
  );
  const LETTERS_REGEX = /^[A-Za-z\u0600-\u06FF\s]+$/;
  // comment: schema with humanAnswer field
const validationSchema = Yup.object({
  full_name: Yup.string()
    .matches(LETTERS_REGEX, t("validation.lettersOnly") || "Only letters are allowed")
    .required(t("validation.Full_Name")),
  last_name: Yup.string()
    .matches(LETTERS_REGEX, t("validation.lettersOnly") || "Only letters are allowed")
    .required(t("validation.Company_Name")),
  phone: Yup.string().required(t("validation.Phone_Number")),
  email: Yup.string().email(t("Invalid Email")).required(t("Email is required")),
  message: Yup.string(),
  humanAnswer: Yup.string()
    .required(t("validation.recaptcha"))
    .test("math-match", t("validation.recaptchaWrong"), (val) => {
      const num = Number(String(val || "").trim());
      return !Number.isNaN(num) && num === expected;
    }),
});


  // comment: remove humanAnswer from payload; keep same API usage
  const handleSubmit = async (values, { resetForm }) => {
    try {
      const { humanAnswer, ...rest } = values; // do not send humanAnswer
      const payload = {
        ...rest,
        service_id: parseInt(id),
        type: name, // preserve your current behavior
      };
      await contentApi.sendContactMessage(payload);
      toast.success(t("Message sent successfully"));
      resetForm();
      setChallenge(genChallenge()); // new challenge next submit
    } catch (error) {
      toast.error(t("Error sending message"));
      console.error(error);
    }
  };

  return (
    <>
      <HelmetInfo titlePage={t("Request Service")} />
      <ToastContainer />
      <div className="mt-[150px]" />
      <div className="main-container bg-[#fff] mx-auto px-4 py-8 min-h-[1000px]">
        {/* Header Section */}
        <div className="flex flex-col items-center gap-12 max-w-screen-lg mx-auto pb-5">
          <BreadCrumb />
          <div className="text-center flex flex-col gap-6">
            <h1 className="text-4xl max-sm:text-[1.25rem] text_bold_Bukra text-[#0d6b70] leading-[80px]">
              {t("Register for the service")}
            </h1>
          </div>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={{
            full_name: "",
            last_name: "",
            type: "",
            phone: "",
            email: "",
            message: "",
            humanAnswer: "", // ✅ new field
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Form className="max-w-5xl mx-auto flex flex-col gap-6 mt-28">
            {/* الاسم + الشركة */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm text-[#1d1d1d] 29LT_Bukra">{t("Full Name")} *</label>
                <Field
                  type="text"
                  name="full_name"
                  placeholder={t("Full Name")}
                  className="w-full h-12 px-4 rounded-2xl bg-[#f5f5f5] outline-none text-right"
                />
                <ErrorMessage name="full_name" component="span" className="text-red-500 text-xs" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm text-[#1d1d1d] 29LT_Bukra">{t("Company Name")} *</label>
                <Field
                  type="text"
                  name="last_name"
                  placeholder={t("Company Name")}
                  className="w-full h-12 px-4 rounded-2xl bg-[#f5f5f5] outline-none text-right"
                />
                <ErrorMessage name="last_name" component="span" className="text-red-500 text-xs" />
              </div>
            </div>

            {/* الهاتف + البريد */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm text-[#1d1d1d] 29LT_Bukra">{t("Phone Number")} *</label>
                <Field
                  type="number"
                  name="phone"
                  placeholder={t("Phone Number")}
                  className="w-full h-12 px-4 rounded-2xl bg-[#f5f5f5] outline-none text-right"
                />
                <ErrorMessage name="phone" component="span" className="text-red-500 text-xs" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm text-[#1d1d1d] 29LT_Bukra">{t("Email")} *</label>
                <Field
                  type="email"
                  name="email"
                  placeholder={t("Email")}
                  className="w-full h-12 px-4 rounded-2xl bg-[#f5f5f5] outline-none text-right"
                />
                <ErrorMessage name="email" component="span" className="text-red-500 text-xs" />
              </div>
            </div>

            {/* الرسالة */}
            <div className="flex flex-col gap-2">
              <label className="text-sm text-[#1d1d1d] 29LT_Bukra">{t("Message")}</label>
              <Field
                as="textarea"
                name="message"
                placeholder={t("Leave us a message...")}
                rows={5}
                className="w-full px-4 py-3 rounded-2xl bg-[#f5f5f5] outline-none text-right resize-none"
              />
              <ErrorMessage name="message" component="span" className="text-red-500 text-xs" />
            </div>

            {/* ✅ ريكابشن */}
            <div className="flex flex-col gap-2">
              <label className="text-sm text-[#1d1d1d] 29LT_Bukra">
                {t("fields.recaptcha")} ({challenge.a} {challenge.op} {challenge.b} = ?)
              </label>
              <Field
                name="humanAnswer"
                inputMode="numeric"
                placeholder={t("placeholders.recaptcha")}
                className="w-full h-12 px-4 rounded-2xl bg-[#f5f5f5] outline-none text-right"
              />
              <ErrorMessage name="humanAnswer" component="span" className="text-red-500 text-xs" />
            </div>

            {/* إرسال */}
            <button
              type="submit"
              className="w-full h-12 bg-[#0d6b70] text-white rounded-full text-center font-bold mt-12"
            >
              {t("Send")}
            </button>
          </Form>
        </Formik>
      </div>
    </>
  );
};

export default FormService;
